import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import apiClient from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Plus,
  Minus,
  ShoppingCart,
  CreditCard,
  DollarSign,
  Check,
  CheckCircle,
  Search,
  Package,
  Users,
  Receipt,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { X } from "lucide-react";
import { toastHelpers } from "@/lib/toast-helpers";
import { formatCurrency } from "@/lib/utils";

import type {
  Order,
  ProcessPaymentRequest,
  CreateOrderRequest,
  CartItem,
  UpdateOrderRequest,
  User,
  Stock,
} from "@/types";

import { OrderStatus } from "@/types";
import { useRouter } from "@tanstack/react-router";
import { useNavigationRefresh } from "@/hooks/useNavigationRefresh";

function CounterProductSkeleton() {
  return (
    <Card className="hover:shadow-lg transition-shadow aspect-square flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4 border-b">
        <Skeleton className="w-16 h-16 rounded-lg" />
      </div>
      <CardContent className="p-3 space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-3 w-3/4" />
        <div className="flex items-center justify-between pt-2">
          <Skeleton className="h-6 w-16" />
          <Skeleton className="h-8 w-20" />
        </div>
      </CardContent>
    </Card>
  );
}

function CounterOrderSkeleton() {
  return (
    <Card className="transition-all hover:shadow-md">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Skeleton className="h-10 w-10 rounded" />
            <div className="space-y-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-4 w-4" />
          </div>
        </div>
        <div className="mt-3 pt-3 border-t space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      </CardContent>
    </Card>
  );
}

export function CounterInterface() {
  const { t } = useTranslation();
  const router = useRouter();

  const queryClient = useQueryClient();

  // Responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [showMobileCart, setShowMobileCart] = useState(false);

  // State
  const [user, setUser] = useState<User>();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [existingOrderItems, setExistingOrderItems] = useState<CartItem[]>([]); // Track existing order items separately
  const [activeTab, setActiveTab] = useState<"create" | "payment">("create");
  const [orderType, setOrderType] = useState<"walk_in">("walk_in");
  const [orderNotes, setOrderNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set()); // Track which orders are expanded
  const [paymentMethod, setPaymentMethod] = useState<
    "cash" | "card" | "insurance" | "others"
  >("cash");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");

  const { manualRefresh, isRefreshing } = useNavigationRefresh([
    "stocks",
    "categories",
    "orders",
  ]);

  // Responsive breakpoint detection
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    const decodedToken = apiClient.isAuthenticated();

    if (decodedToken) {
      setUser(decodedToken);
    } else {
      toastHelpers.sessionExpired();
      router.navigate({ to: "/login" });
    }
  }, []);

  // Set payment amount to order total when order is selected
  useEffect(() => {
    if (selectedOrder && activeTab === "payment") {
      const breakdown = getOrderPaymentBreakdown(selectedOrder);
      setPaymentAmount(breakdown.total.toString());
    }
  }, [selectedOrder, activeTab]);

  // Data fetching
  const { data: stocks, isLoading: loadingStocks } = useQuery({
    queryKey: ["Stocks"],
    queryFn: () =>
      apiClient.getStocks().then((res: any) => {
        console.log(res.data);
        return (res.data?.stocks || []) as Stock[];
      }),
  });

  const { data: categories, isLoading: loadingCategories } = useQuery({
    queryKey: ["categories"],
    queryFn: () =>
      apiClient.getCategories().then((res) => {
        // console.log("Categories:", res.data);
        return res.data;
      }),
  });

  const { data: orders, isLoading: loadingOrders } = useQuery({
    queryKey: ["orders"],
    queryFn: () =>
      apiClient.getOrders({ status: "confirmed" }).then((res) => {
        console.log("Orders:", res.data);
        return res.data;
      }),
    enabled: activeTab === "payment",
  });

  // Show loading screen when backend is called (not cache fetch)
  const isLoadingAny =
    isRefreshing || loadingStocks || loadingCategories || loadingOrders;

  // Order Create Mutations
  const createOrderMutation = useMutation({
    mutationFn: (orderData: CreateOrderRequest) =>
      apiClient.createCounterOrder(orderData),
    onSuccess: () => {
      setCart([]);
      setSelectedOrder(null); // Clear selected order
      setOrderNotes("");
      setOrderType("walk_in");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toastHelpers.success(
        t("Counter.Toast.TreatmentCreatedTitle"),
        t("Counter.Toast.TreatmentCreated")
      );
    },
    onError: (err: any) => {
      console.error("Create order failed:", err);
      toastHelpers.error(
        t("Counter.Toast.TreatmentCreationFailed"),
        err.message || t("Counter.Toast.TreatmentCreationFailed")
      );
    },
  });

  // order update Mutation
  const updateOrderMutation = useMutation({
    mutationFn: (orderData: UpdateOrderRequest) =>
      apiClient.updateCounterOrder(orderData),
    onSuccess: () => {
      setCart([]);
      setSelectedOrder(null); // Clear selected order
      setExistingOrderItems([]);
      setOrderNotes("");
      setOrderType("walk_in");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      console.log("Order updated successfully");
    },
    onError: (err: any) => {
      console.error("Update order failed:", err);
      toastHelpers.error(
        t("Counter.Toast.TreatmentUpdateFailed"),
        t("Counter.Toast.TreatmentUpdateFailed")
      );
    },
  });

  const processPaymentMutation = useMutation({
    mutationFn: ({
      orderId,
      paymentData,
    }: {
      orderId: string;
      paymentData: ProcessPaymentRequest;
    }) => apiClient.processCounterPayment(orderId, paymentData),
    onSuccess: (_data, vars) => {
      // try to print receipt for the paid order
      console.log("processPaymentMutation onSuccess", vars);
      try {
        if (vars?.orderId) void printReceipt(vars.orderId);
      } catch (e) {
        console.error("printReceipt failed", e);
      }

      setSelectedOrder(null);
      setCart([]);
      setPaymentAmount("");
      setReferenceNumber("");
      setOrderType("walk_in");
      setActiveTab("create");
      setExistingOrderItems([]);
      // Show payment completed toast
      try {
        toastHelpers.success(
          t("Counter.Toast.PaymentCompletedTitle"),
          t("Counter.Toast.PaymentCompleted", {
            id: vars.orderId,
            amount: formatCurrency(Number(vars.paymentData.amount)),
            method: vars.paymentData.payment_method,
          })
        );
      } catch (e) {
        console.error("Toast failed", e);
      }
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (err: any) => {
      console.error("Payment processing failed:", err);
      toastHelpers.error(
        t("Counter.Toast.PaymentProcessingFailed"),
        t("Counter.Toast.PaymentProcessingFailed")
      );
    },
  });

  const cancelOrderMutation = useMutation({
    mutationFn: (orderId: string) =>
      apiClient.updateOrderStatus(orderId, OrderStatus.CANCELLED),
    onSuccess: () => {
      setSelectedOrder(null);
      setCart([]);
      setExistingOrderItems([]);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
      toastHelpers.success(
        t("Counter.Toast.TreatmentCancelledTitle"),
        t("Counter.Toast.TreatmentCancelled")
      );
      setActiveTab("create");
      setOrderType("walk_in");
    },
    onError: (err: any) => {
      toastHelpers.error(
        t("Counter.Toast.CancelFailed"),
        err?.message || t("Counter.Toast.CancelFailed")
      );
    },
  });

  // Helper functions
  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const addToCart = (stock: Stock) => {
    setCart((prev) => {
      const existingItem = prev.find((item) => item.Stock.id === stock.id);
      if (existingItem) {
        return prev.map((item) =>
          item.Stock.id === stock.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { Stock: stock, quantity: 1 }];
    });
  };

  const removeFromCart = (stockId: string) => {
    setCart((prev) => {
      const existingItem = prev.find((item) => item.Stock.id === stockId);
      if (existingItem && existingItem.quantity > 1) {
        return prev.map((item) =>
          item.Stock.id === stockId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }
      return prev.filter((item) => item.Stock.id !== stockId);
    });
  };

  const getTotalAmount = () => {
    return cart.reduce(
      (total, item) => total + item.Stock.cost_per_unit * item.quantity,
      0
    );
  };

  const getPaymentBreakdown = () => {
    const subtotal = getTotalAmount();
    const serviceCharge = 0; // No service charge for dental treatments
    const total = subtotal + serviceCharge;

    return {
      subtotal,
      serviceCharge,
      total,
    };
  };

  const getOrderPaymentBreakdown = (order: Order) => {
    if (!order.OrderItems) return { subtotal: 0, serviceCharge: 0, total: 0 };

    const subtotal = order.OrderItems.reduce((sum, item) => {
      return sum + item.quantity * (item.Stock?.cost_per_unit || 0);
    }, 0);
    const serviceCharge = 0; // No service charge for dental treatments
    const total = subtotal + serviceCharge;

    return {
      subtotal,
      serviceCharge,
      total,
    };
  };

  // Filtered data
  const filteredStocks = stocks?.filter((stock: Stock) => {
    const matchesSearch = stock.name
      .toLowerCase()
      .startsWith(searchTerm.toLowerCase());
    console.log("selectedCategory", selectedCategory);
    const matchesCategory =
      selectedCategory === "all" ||
      String(stock.category_id) == selectedCategory;

    console.log("matchCategory", String(stock.category_id) == selectedCategory);
    return matchesSearch && matchesCategory && stock.is_available;
  });

  // Event handlers
  const handleCreateOrder = async () => {
    if (cart.length === 0) {
      return toastHelpers.error(
        t("Counter.Toast.NoServicesSelected"),
        t("Counter.Toast.AddServicesFirst")
      );
    }
    // If editing an existing order, call update mutation
    if (selectedOrder) {
      const updateData: UpdateOrderRequest = {
        order_id: selectedOrder.id,
        items: cart.map((item) => ({
          stock_id: item.Stock.id,
          quantity: item.quantity,
        })),
        notes: orderNotes,
      };
      updateOrderMutation.mutate(updateData);
      return;
    }

    // Otherwise create a new order
    const orderData: CreateOrderRequest = {
      order_type: orderType,
      items: cart.map((item) => ({
        stock_id: item.Stock.id,
        quantity: item.quantity,
      })),
      notes: orderNotes,
    };

    createOrderMutation.mutate(orderData);
  };

  const handleProcessPayment = async () => {
    if (!selectedOrder || !paymentAmount) return;

    const paymentData: ProcessPaymentRequest = {
      payment_method: paymentMethod,
      amount: parseFloat(paymentAmount),
    };

    if (paymentMethod !== "cash" && referenceNumber) {
      paymentData.reference_number = referenceNumber;
    }

    processPaymentMutation.mutate({
      orderId: selectedOrder.id,
      paymentData,
    });
  };

  // Print a comprehensive receipt for an order by fetching the order then opening a print window
  const printReceipt = async (orderId: string) => {
    try {
      const res = await apiClient.getOrder(orderId);
      const order = res?.data;

      console.log("Order data:", order);
      if (!order) return;

      const anyOrder: any = order as any;

      // Extract invoice details
      const invoice = anyOrder.Invoice || {};
      const items = anyOrder.OrderItems || [];

      // Invoice information
      const invoiceNumber = invoice.invoice_number || `INV-${order.id}`;
      const amountPaid = order.Receipt?.amount_paid || "0.00";
      const changeAmount = order.Receipt?.change_given || "0.00";
      const discountAmount = invoice.discount_amount || "0.00";
      const serviceChargeAmount = invoice.service_charge_amount || "0.00";
      const serviceChargePercentage =
        invoice.service_charge_percentage || "0.00";
      const subtotal = invoice.subtotal || "0.00";
      const totalAmount = invoice.total_amount || "0.00";
      const paymentMethod = order.Receipt?.payment_method || "cash";
      const paymentStatus = invoice.payment_status || "pending";
      const notes = invoice.notes || order.notes || "";

      // Format dates
      const treatmentDate = order.created_at
        ? new Date(order.created_at).toLocaleString()
        : "-";
      const paidDate = invoice.paid_at
        ? new Date(invoice.paid_at).toLocaleString()
        : "-";
      const dueDate = invoice.due_date
        ? new Date(invoice.due_date).toLocaleString()
        : "-";

      // Treatment information
      const appointmentType = (order.order_type || "")
        .replace("_", " ")
        .toUpperCase();

      // Build service rows with comprehensive details
      const rows = items
        .map((item: any) => {
          const serviceName = item.Stock?.name || item.stock?.name || "Service";
          const quantity = item.quantity || 1;
          const unitPrice =
            item.Stock?.cost_per_unit || item.cost_per_unit || 0;
          const lineTotal = quantity * unitPrice;

          return `<tr>
            <td>${serviceName}</td>
            <td style="text-align:center">${quantity}</td>
            <td style="text-align:right">${formatCurrency(unitPrice)}</td>
            <td style="text-align:right">${formatCurrency(lineTotal)}</td>
          </tr>`;
        })
        .join("");

      const html = `
        <html>
          <head>
            <title>Invoice #${invoiceNumber}</title>
            <style>
              body{font-family:Arial,Helvetica,sans-serif;padding:20px;color:#111;max-width:450px;margin:0 auto}
              .header{text-align:center;border-bottom:2px solid #333;padding-bottom:10px;margin-bottom:15px}
              h2{margin:0 0 5px;font-size:24px}
              .business-name{font-size:18px;font-weight:bold;margin-bottom:3px}
              .invoice-info{margin-bottom:15px}
              .info-row{display:flex;justify-content:space-between;margin-bottom:3px;font-size:13px}
              .info-section{margin-bottom:15px;padding-bottom:10px;border-bottom:1px solid #eee}
              table{width:100%;border-collapse:collapse;margin:10px 0}
              td,th{padding:6px 4px;border-bottom:1px solid #ddd;font-size:12px}
              th{background:#f5f5f5;font-weight:bold}
              .totals{border-top:2px solid #333;padding-top:10px;margin-top:10px}
              .total-row{display:flex;justify-content:space-between;margin-bottom:4px;font-size:13px}
              .final-total{font-weight:bold;font-size:16px;border-top:1px solid #333;padding-top:6px;margin-top:6px}
              .payment-details{margin-top:15px;padding-top:10px;border-top:1px solid #ddd}
              .footer{text-align:center;margin-top:20px;font-size:12px;color:#666}
              .notes{margin-top:10px;font-style:italic;font-size:12px;background:#f9f9f9;padding:8px;border-radius:4px}
              .status{padding:2px 6px;border-radius:3px;font-size:11px;font-weight:bold}
              .status-paid{background:#d4edda;color:#155724}
              .status-pending{background:#fff3cd;color:#856404}
            </style>
          </head>
          <body>
            <div class="header">
              <div class="business-name">SmileFull</div>
              <div style="font-size:14px;color:#666">#${invoiceNumber}</div>
            </div>
            
            <div class="info-section">
              <div class="info-row">
                <span><strong>Treatment #:</strong></span>
                <span>${order.id}</span>
              </div>
              <div class="info-row">
                <span><strong>Appointment Type:</strong></span>
                <span>${appointmentType}</span>
              </div>
              <div class="info-row">
                <span><strong>Treatment Date:</strong></span>
                <span>${treatmentDate}</span>
              </div>
              <div class="info-row">
                <span><strong>Status:</strong></span>
                <span class="status ${paymentStatus === "paid" ? "status-paid" : "status-pending"}">${paymentStatus.toUpperCase()}</span>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th style="text-align:left">Service</th>
                  <th style="text-align:center">Qty</th>
                  <th style="text-align:right">Unit Price</th>
                  <th style="text-align:right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${rows}
              </tbody>
            </table>

            <div class="totals">
              <div class="total-row">
                <span>Subtotal:</span>
                <span>${formatCurrency(Number(subtotal))}</span>
              </div>
              ${
                Number(discountAmount) > 0
                  ? `
              <div class="total-row" style="color:#d63384">
                <span>Discount:</span>
                <span>-${formatCurrency(Number(discountAmount))}</span>
              </div>
              `
                  : ""
              }
              ${
                Number(serviceChargeAmount) > 0
                  ? `
              <div class="total-row">
                <span>Service Charge (${serviceChargePercentage}%):</span>
                <span>${formatCurrency(Number(serviceChargeAmount))}</span>
              </div>
              `
                  : ""
              }
              <div class="total-row final-total">
                <span>Total Amount:</span>
                <span>${formatCurrency(Number(totalAmount))}</span>
              </div>
            </div>

            ${
              paymentStatus === "paid"
                ? `
            <div class="payment-details">
              <div class="info-row">
                <span><strong>Payment Method:</strong></span>
                <span>${paymentMethod.toUpperCase()}</span>
              </div>
              <div class="info-row">
                <span><strong>Amount Paid:</strong></span>
                <span>${formatCurrency(Number(amountPaid))}</span>
              </div>
              ${
                Number(changeAmount) > 0
                  ? `
              <div class="info-row">
                <span><strong>Change:</strong></span>
                <span>${formatCurrency(Number(changeAmount))}</span>
              </div>
              `
                  : ""
              }
              ${
                paidDate !== "-"
                  ? `
              <div class="info-row">
                <span><strong>Payment Date:</strong></span>
                <span>${paidDate}</span>
              </div>
              `
                  : ""
              }
            </div>
            `
                : `
            <div class="payment-details">
              <div class="info-row">
                <span><strong>Payment Status:</strong></span>
                <span class="status status-pending">PENDING</span>
              </div>
              ${
                dueDate !== "-"
                  ? `
              <div class="info-row">
                <span><strong>Due Date:</strong></span>
                <span>${dueDate}</span>
              </div>
              `
                  : ""
              }
            </div>
            `
            }

            ${
              notes
                ? `
            <div class="notes">
              <strong>Notes:</strong> ${notes}
            </div>
            `
                : ""
            }

            <div class="footer">
              <p>Thank you for choosing our dental clinic!</p>
              <p>We wish you good health</p>
              ${paymentStatus !== "paid" ? '<p style="color:#856404;font-weight:bold">** PAYMENT PENDING **</p>' : ""}
            </div>
          </body>
        </html>
      `;

      const w = window.open("", "_blank", "width=500,height=900");
      if (!w) return;
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      // Delay to allow resources to render
      setTimeout(() => {
        try {
          w.print();
        } catch (e) {
          console.error("Print failed", e);
        }
        try {
          w.close();
        } catch {}
      }, 500);
    } catch (err) {
      console.error("Failed to print receipt", err);
      // Show error toast to user
      toastHelpers.error(
        t("Counter.Toast.ReceiptPrintFailed"),
        t("Counter.Toast.ReceiptPrintFailedMessage")
      );
    }
  };

  const getOrderTypeIcon = (type: string) => {
    switch (type) {
      case "walk_in":
        return <Users className="w-4 h-4" />;
      case "scheduled":
        return <Check className="w-4 h-4" />;
      case "emergency":
        return <CreditCard className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getOrderTypeBadge = (type: string) => {
    const configs = {
      walk_in: { label: "Walk-In", color: "bg-blue-200 text-blue-900" },
      scheduled: { label: "Scheduled", color: "bg-green-200 text-green-900" },
      emergency: { label: "Emergency", color: "bg-red-200 text-red-900" },
    };
    const config = configs[type as keyof typeof configs] || configs.walk_in;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  return (
    <div
      className={`flex ${isMobile ? "flex-col" : ""} bg-background ${user?.role === "admin" ? "h-screen" : "h-[92vh]"}`}
    >
      {/* Left Side - Header with Tabs and Order Items */}
      <div
        className={`${isMobile ? "w-full" : isTablet ? "w-3/5" : "w-full"} border-r border-border overflow-hidden flex flex-col`}
      >
        {/* Header with Tabs */}
        <div
          className={`${isMobile ? "p-3" : isTablet ? "p-3" : "p-4"} border-b border-border bg-card`}
        >
          <div
            className={`flex ${isMobile ? "flex-col space-y-3" : isTablet ? "flex-col space-y-2" : "items-center justify-between"} ${isMobile ? "mb-3" : isTablet ? "mb-2" : "mb-6"}`}
          >
            <div>
              <h1
                className={`${isMobile ? "text-xl" : isTablet ? "text-xl" : "text-2xl"} font-bold`}
              >
                {t("Counter.Heading")}
              </h1>
              <p
                className={`text-muted-foreground ${isMobile || isTablet ? "text-xs" : ""}`}
              >
                {t("Counter.Subheading")}
              </p>
            </div>
            <div
              className={`flex ${isMobile || isTablet ? "w-full" : ""} gap-2`}
            >
              <Button
                variant="outline"
                size="sm"
                onClick={manualRefresh}
                disabled={isRefreshing}
                className={`flex items-center gap-2 ${isMobile || isTablet ? "flex-1" : ""}`}
              >
                <RefreshCw
                  className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
                />
                {!isMobile &&
                  !isTablet &&
                  (isRefreshing
                    ? t("Counter.Refreshing")
                    : t("Counter.Refresh"))}
              </Button>
              <Button
                variant={activeTab === "create" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setSelectedOrder(null);
                  setCart([]);
                  setPaymentAmount("");
                  setReferenceNumber("");
                  setOrderType("walk_in");
                  setActiveTab("create");
                  setExistingOrderItems([]);
                }}
                className={isMobile || isTablet ? "flex-1" : ""}
              >
                <Plus className="w-4 h-4 mr-1" />
                {isMobile || isTablet
                  ? t("Counter.New")
                  : t("Counter.NewOrder")}
              </Button>
              <Button
                variant={activeTab === "payment" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTab("payment")}
                className={isMobile || isTablet ? "flex-1" : ""}
              >
                <CreditCard className="w-4 h-4 mr-1" />
                {isMobile || isTablet
                  ? t("Counter.PaymentShort")
                  : t("Counter.ProcessPayment")}
              </Button>
            </div>
          </div>

          {activeTab === "create" && (
            <>
              {/* Appointment Type Selection */}
              <div className="flex justify-between mt-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant={orderType === "walk_in" ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setOrderType("walk_in");
                      setExistingOrderItems([]);
                      setCart([]);
                      setSelectedOrder(null);
                    }}
                  >
                    <Users className="w-4 h-4 mr-1" />
                    {t("Counter.AppointmentTypes.WalkIn")}
                  </Button>
                  {/* <Button
                    variant={orderType === "scheduled" ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setOrderType("scheduled");
                      setExistingOrderItems([]);
                      setCart([]);
                      setSelectedOrder(null);
                    }}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    {t("Counter.AppointmentTypes.Scheduled")}
                  </Button>
                  <Button
                    variant={orderType === "emergency" ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setOrderType("emergency");
                      setExistingOrderItems([]);
                      setCart([]);
                      setSelectedOrder(null);
                    }}
                  >
                    <CreditCard className="w-4 h-4 mr-1" />
                    {t("Counter.AppointmentTypes.Emergency")}
                  </Button> */}
                </div>

                <div />
              </div>
            </>
          )}
        </div>

        {/* Content Area - Stock View */}
        {activeTab === "create" ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Product Selection View */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div
                className={`${isMobile ? "p-3" : isTablet ? "p-3" : "p-4"} border-b border-border`}
              >
                {/* Search */}
                <div className={`${isMobile || isTablet ? "mb-2" : "mb-3"}`}>
                  <div className="relative">
                    <Search
                      className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground ${isMobile || isTablet ? "h-3 w-3" : "h-4 w-4"}`}
                    />
                    <Input
                      placeholder={t("Counter.Search.Placeholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`${isMobile || isTablet ? "pl-9 text-sm h-9" : "pl-10"}`}
                    />
                  </div>
                </div>

                {/* Category Filter */}
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  <Button
                    variant={selectedCategory === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory("all")}
                  >
                    {t("Counter.Categories.AllServices")}
                  </Button>
                  {isLoadingAny
                    ? Array.from({ length: 4 }, (_, i) => (
                        <Skeleton key={i} className="h-8 w-20" />
                      ))
                    : categories?.map((category) => {
                        console.log("Category:", category);
                        return (
                          <Button
                            key={category.id}
                            variant={
                              selectedCategory === category.id
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            onClick={() => setSelectedCategory(category.id)}
                          >
                            {category.name}
                          </Button>
                        );
                      })}
                </div>
              </div>

              {/* Stocks Grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {isLoadingAny ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {Array.from({ length: 12 }, (_, i) => (
                      <CounterProductSkeleton key={i} />
                    ))}
                  </div>
                ) : filteredStocks?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t("Counter.EmptyStates.NoServicesFound")}</p>
                    <p className="text-sm">
                      {searchTerm || selectedCategory !== "all"
                        ? t("Counter.EmptyStates.TryAdjusting")
                        : t("Counter.EmptyStates.NoServicesAvailable")}
                    </p>
                  </div>
                ) : (
                  <div
                    className={`grid ${isMobile ? "grid-cols-2 gap-3" : isTablet ? "grid-cols-3 gap-3" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2"}`}
                  >
                    {filteredStocks?.map((stock) => {
                      const cartItem = cart.find(
                        (item) => item.Stock.id === stock.id
                      );
                      return (
                        <Card
                          key={stock.id}
                          className="hover:shadow-lg transition-shadow aspect-square flex flex-col"
                        >
                          {/* Product Image */}
                          {stock.image_url ? (
                            <div className="relative flex-1 overflow-hidden rounded-t-lg">
                              <img
                                src={stock.image_url}
                                alt={stock.name}
                                className="w-full h-full object-fit"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            </div>
                          ) : (
                            <div className="flex-1 w-full rounded-t-lg bg-gradient-to-r from-gray-400 to-gray-600 flex items-center justify-center">
                              <Package className="h-8 w-8 text-white" />
                            </div>
                          )}

                          <CardHeader
                            className={`${isMobile || isTablet ? "p-2" : "p-2"} flex-shrink-0`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <CardTitle
                                  className={`${isMobile || isTablet ? "text-xs" : "text-sm"} leading-tight truncate`}
                                >
                                  {stock.name}
                                </CardTitle>
                                {/* {stock.description && (
                                  <CardDescription className="text-xs mt-1 line-clamp-1">
                                    {stock.description.substring(0, 25)}
                                    {stock.description.length > 25
                                      ? "..."
                                      : ""}
                                  </CardDescription>
                                )} */}
                              </div>
                              <div
                                className={`${isMobile || isTablet ? "text-xs" : "text-sm"} font-bold text-black ml-2`}
                              >
                                {formatCurrency(stock.cost_per_unit)}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent
                            className={`${isMobile || isTablet ? "p-2" : "p-2"} pt-0 flex-shrink-0 mt-auto`}
                          >
                            <div className="flex items-center justify-between">
                              {stock.is_available && (
                                <div className="flex items-center gap-1 w-full">
                                  {cartItem ? (
                                    <div className="flex items-center gap-1 w-full">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => removeFromCart(stock.id)}
                                        className="h-6 w-6 p-0"
                                      >
                                        <Minus className="h-2 w-2" />
                                      </Button>
                                      <span className="w-6 text-center font-medium text-xs">
                                        {cartItem.quantity}
                                      </span>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => addToCart(stock)}
                                        className="h-6 w-6 p-0"
                                      >
                                        <Plus className="h-2 w-2" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={() => addToCart(stock)}
                                      className="w-full h-6 text-xs"
                                    >
                                      <Plus className="h-2 w-2 mr-1" />
                                      Add
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Payment Tab Content */
          <div className="flex-1 overflow-y-auto">
            <div
              className={`${isMobile || isTablet ? "p-3" : "p-4"} space-y-${isMobile || isTablet ? "3" : "4"}`}
            >
              <h3
                className={`${isMobile || isTablet ? "text-base" : "text-lg"} font-semibold ${isMobile || isTablet ? "mb-3" : "mb-4"}`}
              >
                {t("Counter.Payment.Heading")}
              </h3>
              {isLoadingAny ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }, (_, i) => (
                    <CounterOrderSkeleton key={i} />
                  ))}
                </div>
              ) : orders?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt
                    className={`${isMobile || isTablet ? "w-10 h-10" : "w-12 h-12"} mx-auto mb-3 opacity-50`}
                  />
                  <p className={isMobile || isTablet ? "text-sm" : ""}>
                    {t("Counter.EmptyStates.NoTreatmentsForPayment")}
                  </p>
                </div>
              ) : (
                orders?.map((order) => {
                  const isExpanded = expandedOrders.has(order.id);
                  return (
                    <Card
                      key={order.id}
                      className={`transition-all ${
                        selectedOrder?.id === order.id
                          ? "ring-2 ring-primary"
                          : "hover:shadow-md"
                      }`}
                    >
                      <CardContent
                        className={isMobile || isTablet ? "p-3" : "p-4"}
                      >
                        <div
                          className="flex items-center justify-between cursor-pointer"
                          onClick={() => {
                            setSelectedOrder(order);
                            setPaymentAmount((order.price ?? 0).toString());
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {getOrderTypeIcon(order.order_type)}
                            <div>
                              <div
                                className={`font-semibold ${isMobile || isTablet ? "text-sm" : ""}`}
                              >
                                {t("Counter.Payment.TreatmentNumber")}
                                {order.id}
                              </div>
                              <div
                                className={`${isMobile || isTablet ? "text-xs" : "text-sm"} text-muted-foreground`}
                              >
                                {order.OrderItems?.length || 0}{" "}
                                {t("Counter.TreatmentPlan.Services")}
                              </div>
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-2">
                            <div>
                              <div
                                className={`${isMobile || isTablet ? "text-base" : "text-lg"} font-bold`}
                              >
                                {formatCurrency(order.price ?? 0)}
                              </div>
                              <div
                                className={`flex items-center ${isMobile || isTablet ? "gap-1" : "gap-2"}`}
                              >
                                {!isMobile &&
                                  !isTablet &&
                                  getOrderTypeBadge(order.order_type)}
                                <Badge
                                  variant="outline"
                                  className={
                                    isMobile || isTablet
                                      ? "text-[10px] px-1.5 py-0"
                                      : "text-xs"
                                  }
                                >
                                  {order.status}
                                </Badge>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleOrderExpansion(order.id);
                              }}
                              className={`${isMobile || isTablet ? "ml-1" : "ml-2"} ${isMobile || isTablet ? "h-8 w-8 p-0" : ""}`}
                            >
                              {isExpanded ? (
                                <ChevronDown
                                  className={`${isMobile || isTablet ? "w-3 h-3" : "w-4 h-4"}`}
                                />
                              ) : (
                                <ChevronRight
                                  className={`${isMobile || isTablet ? "w-3 h-3" : "w-4 h-4"}`}
                                />
                              )}
                            </Button>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <div className="space-y-3">
                              {/* Order Information */}
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <div className="font-medium text-muted-foreground">
                                    {t("Counter.Payment.TreatmentDetails")}
                                  </div>
                                  <div>{order.id}</div>
                                </div>
                                <div>
                                  <div className="font-medium text-muted-foreground">
                                    {t("Counter.Payment.Status")}
                                  </div>
                                  <div className="capitalize">
                                    {order.status}
                                  </div>
                                </div>
                                <div>
                                  <div className="font-medium text-muted-foreground">
                                    {t("Counter.Payment.AppointmentType")}
                                  </div>
                                  <div className="capitalize">
                                    {order.order_type.replace("_", " ")}
                                  </div>
                                </div>
                                {order.created_at && (
                                  <div>
                                    <div className="font-medium text-muted-foreground">
                                      Created At
                                    </div>
                                    <div>
                                      {new Date(
                                        order.created_at
                                      ).toLocaleString()}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Treatment Services */}
                              {order.OrderItems &&
                                order.OrderItems.length > 0 && (
                                  <div>
                                    <div className="font-medium text-muted-foreground mb-2">
                                      {t("Counter.Payment.ServicesProvided")}
                                    </div>
                                    <div className="space-y-2">
                                      {order.OrderItems.map((item, index) => (
                                        <div
                                          key={index}
                                          className="flex items-center justify-between p-2 bg-gray-50 rounded"
                                        >
                                          <div className="flex-1">
                                            <div className="font-medium">
                                              {item.Stock?.name ||
                                                "Unknown Service"}
                                            </div>
                                            <div className="text-sm text-muted-foreground">
                                              {formatCurrency(
                                                item.Stock?.cost_per_unit || 0
                                              )}{" "}
                                              × {item.quantity}
                                            </div>
                                          </div>
                                          <div className="font-medium">
                                            {formatCurrency(
                                              (item.Stock?.cost_per_unit || 0) *
                                                item.quantity
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                              {/* Notes */}
                              {order.notes && (
                                <div>
                                  <div className="font-medium text-muted-foreground">
                                    {t("Counter.Payment.Notes")}
                                  </div>
                                  <div className="text-sm bg-gray-50 p-2 rounded">
                                    {order.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right Side - Cart and Order Details - Hidden on mobile, visible on tablet and desktop */}
      <div
        className={`${isMobile ? "hidden" : isTablet ? "w-2/5" : "w-1/2"} flex flex-col bg-card border-l border-border`}
      >
        {activeTab === "create" ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Cart Section */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                <h3 className="font-semibold mb-3 flex items-center">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {selectedOrder ? (
                    <>
                      <div className="flex items-center gap-2">
                        <div>
                          {t("Counter.TreatmentPlan.EditingTreatment")} #
                          {selectedOrder.id} (
                          {cart.length + existingOrderItems.length}{" "}
                          {t("Counter.TreatmentPlan.Services")})
                        </div>
                        <Badge
                          className="ml-2 bg-gray-200 text-gray-800"
                          variant="secondary"
                        >
                          {t("Counter.Badges.Editing")}
                        </Badge>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (!selectedOrder) return;
                            if (!confirm(t("Counter.Confirm.CancelTreatment")))
                              return;
                            cancelOrderMutation.mutate(selectedOrder.id);
                          }}
                          className="ml-2 bg-red-600 hover:bg-red-700 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                        >
                          {cancelOrderMutation.isPending ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" />
                              {t("Counter.Actions.Cancelling")}
                            </>
                          ) : (
                            <>
                              <X className="w-3 h-3 mr-1" />
                              {t("Counter.Actions.CancelTreatment")}
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {t("Counter.TreatmentPlan.Title")} ({cart.length})
                    </>
                  )}
                </h3>

                {/* Existing Treatment Items Section */}
                {existingOrderItems.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold mb-2 flex items-center text-gray-800">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {t("Counter.TreatmentPlan.ExistingServices")}
                    </h4>
                    <div className="space-y-2">
                      {existingOrderItems.map((item, index) => (
                        <div
                          key={`existing-${index}`}
                          className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate text-gray-900">
                              {item.Stock?.name ||
                                t("Counter.TreatmentPlan.UnknownService")}
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatCurrency(item.Stock?.cost_per_unit || 0)} ×{" "}
                              {item.quantity}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <div className="font-medium text-gray-800">
                              {formatCurrency(
                                (item.Stock?.cost_per_unit || 0) * item.quantity
                              )}
                            </div>
                            <Badge
                              variant="secondary"
                              className="bg-gray-200 text-gray-800"
                            >
                              {t("Counter.TreatmentPlan.Existing")}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 p-2 bg-gray-100 rounded text-sm font-medium text-gray-800">
                      {t("Counter.TreatmentPlan.Total")}{" "}
                      {formatCurrency(
                        existingOrderItems.reduce(
                          (sum, item) =>
                            sum +
                            (item.Stock?.cost_per_unit || 0) * item.quantity,
                          0
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* New Services Section */}
                {cart.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold mb-2 flex items-center text-gray-800">
                      <Plus className="w-4 h-4 mr-2" />
                      {t("Counter.TreatmentPlan.NewServices")}
                    </h4>
                    <div className="space-y-2">
                      {cart.map((item) => (
                        <div
                          key={item.Stock.id}
                          className="flex items-center justify-between p-3 bg-white border border-gray-300 rounded-lg"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate text-gray-900">
                              {item.Stock.name}
                            </div>
                            <div className="text-sm text-gray-600">
                              {formatCurrency(item.Stock.cost_per_unit)} ×{" "}
                              {item.quantity}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-2">
                            <div className="font-medium text-gray-800">
                              {formatCurrency(
                                item.Stock.cost_per_unit * item.quantity
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFromCart(item.Stock.id)}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-6 text-center text-sm">
                                {item.quantity}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => addToCart(item.Stock)}
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* New Services Summary */}
                    {(() => {
                      const breakdown = getPaymentBreakdown();
                      return (
                        <div className="mt-2 p-3 bg-gray-100 rounded text-sm">
                          <div className="font-medium text-gray-800 mb-2">
                            {t("Counter.TreatmentPlan.NewServicesSummary")}
                          </div>
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between font-medium">
                              <span>{t("Counter.TreatmentPlan.Total")}</span>
                              <span>{formatCurrency(breakdown.total)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Empty state */}
                {cart.length === 0 && existingOrderItems.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{t("Counter.EmptyStates.NoServicesInPlan")}</p>
                    <p className="text-sm">
                      {selectedOrder
                        ? t("Counter.EmptyStates.AddMoreServices")
                        : t("Counter.EmptyStates.AddServicesFromList")}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary and Actions */}
            {cart.length > 0 && (
              <div className="p-4 border-t border-border bg-card">
                <div className="space-y-4">
                  {/* Payment Breakdown */}
                  {(() => {
                    const newItemsBreakdown = getPaymentBreakdown();
                    const existingItemsTotal = existingOrderItems.reduce(
                      (sum, item) =>
                        sum + (item.Stock?.cost_per_unit || 0) * item.quantity,
                      0
                    );
                    const combinedTotal =
                      newItemsBreakdown.total + existingItemsTotal;

                    return (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-lg font-semibold">
                          <span>Total:</span>
                          <span>{formatCurrency(combinedTotal)}</span>
                        </div>
                      </div>
                    );
                  })()}

                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleCreateOrder}
                    disabled={
                      cart.length === 0 || createOrderMutation.isPending
                    }
                  >
                    {createOrderMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        {selectedOrder
                          ? t("Counter.Actions.UpdateTreatment")
                          : t("Counter.Actions.CreatingTreatment")}
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {selectedOrder ? (
                          <>{t("Counter.Actions.AddServicesToTreatment")}</>
                        ) : (
                          <>
                            {t("Counter.Actions.CreateTreatment", {
                              type:
                                orderType === "walk_in"
                                  ? t("Counter.AppointmentTypes.WalkIn")
                                  : orderType === "scheduled"
                                    ? t("Counter.AppointmentTypes.Scheduled")
                                    : t("Counter.AppointmentTypes.Emergency"),
                            })}
                          </>
                        )}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Payment Tab - Payment Processing Interface */
          <>
            {selectedOrder && (
              <div
                className={`${isMobile ? "p-3" : isTablet ? "p-3" : "p-4"} border-t border-border bg-card space-y-4`}
              >
                <div className="border-b border-border pb-3">
                  <h3
                    className={`font-semibold mb-3 ${isMobile || isTablet ? "text-base" : "text-lg"}`}
                  >
                    {t("Counter.Payment.PaymentDetails")}
                  </h3>
                  <div
                    className={`space-y-2 ${isMobile || isTablet ? "text-xs" : "text-sm"}`}
                  >
                    <div className="flex justify-between">
                      <span>{t("Counter.Payment.Order")}</span>
                      <span className="font-medium">#{selectedOrder.id}</span>
                    </div>
                    {selectedOrder.customer_name && (
                      <div className="flex justify-between">
                        <span>{t("Counter.Payment.Customer")}</span>
                        <span className="font-medium">
                          {selectedOrder.customer_name}
                        </span>
                      </div>
                    )}

                    {/* Payment Breakdown */}
                    {(() => {
                      const breakdown = getOrderPaymentBreakdown(selectedOrder);
                      return (
                        <>
                          <div
                            className={`flex justify-between font-bold ${isMobile || isTablet ? "text-lg" : "text-xl"} border-t pt-2 mt-2`}
                          >
                            <span>{t("Counter.TreatmentPlan.Total")}</span>
                            <span className="text-primary">
                              {formatCurrency(breakdown.total)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div>
                  <label
                    className={`${isMobile || isTablet ? "text-xs" : "text-sm"} font-medium mb-2 block`}
                  >
                    {t("Counter.Payment.PaymentMethod")}
                  </label>
                  <div
                    className={`grid grid-cols-2 ${isMobile || isTablet ? "gap-2" : "gap-2"}`}
                  >
                    <Button
                      variant={paymentMethod === "cash" ? "default" : "outline"}
                      size={isMobile || isTablet ? "sm" : "default"}
                      onClick={() => setPaymentMethod("cash")}
                      className={isMobile || isTablet ? "text-xs" : ""}
                    >
                      <DollarSign
                        className={`${isMobile || isTablet ? "w-3 h-3" : "w-4 h-4"} mr-1`}
                      />
                      {t("Counter.Payment.Methods.Cash")}
                    </Button>
                    <Button
                      variant={paymentMethod === "card" ? "default" : "outline"}
                      size={isMobile || isTablet ? "sm" : "default"}
                      onClick={() => setPaymentMethod("card")}
                      className={isMobile || isTablet ? "text-xs" : ""}
                    >
                      <CreditCard
                        className={`${isMobile || isTablet ? "w-3 h-3" : "w-4 h-4"} mr-1`}
                      />
                      {t("Counter.Payment.Methods.Card")}
                    </Button>
                    <Button
                      variant={
                        paymentMethod === "insurance" ? "default" : "outline"
                      }
                      size={isMobile || isTablet ? "sm" : "default"}
                      onClick={() => setPaymentMethod("insurance")}
                      className={isMobile || isTablet ? "text-xs" : ""}
                    >
                      <ShoppingCart
                        className={`${isMobile || isTablet ? "w-3 h-3" : "w-4 h-4"} mr-1`}
                      />
                      {t("Counter.Payment.Methods.Insurance")}
                    </Button>
                    <Button
                      variant={
                        paymentMethod === "others" ? "default" : "outline"
                      }
                      size={isMobile || isTablet ? "sm" : "default"}
                      onClick={() => setPaymentMethod("others")}
                      className={isMobile || isTablet ? "text-xs" : ""}
                    >
                      {t("Counter.Payment.Methods.Others")}
                    </Button>
                  </div>
                </div>

                <div>
                  <label
                    className={`${isMobile || isTablet ? "text-xs" : "text-sm"} font-medium mb-1 block`}
                  >
                    {t("Counter.Payment.PaymentAmount")}
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className={isMobile || isTablet ? "h-10 text-base" : ""}
                  />
                </div>

                {paymentMethod !== "cash" && (
                  <div>
                    <label
                      className={`${isMobile || isTablet ? "text-xs" : "text-sm"} font-medium mb-1 block`}
                    >
                      {t("Counter.Payment.ReferenceNumber")}
                    </label>
                    <Input
                      placeholder={t("Counter.Payment.TransactionReference")}
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      className={isMobile || isTablet ? "h-10 text-sm" : ""}
                    />
                  </div>
                )}

                <div
                  className={`flex ${isMobile || isTablet ? "flex-col" : "flex-row"} gap-2`}
                >
                  <Button
                    className={isMobile || isTablet ? "w-full" : "flex-1"}
                    size="lg"
                    onClick={handleProcessPayment}
                    disabled={
                      !paymentAmount || processPaymentMutation.isPending
                    }
                  >
                    {processPaymentMutation.isPending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        {t("Counter.Payment.Processing")}
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        {t("Counter.Payment.ProcessPayment")}
                      </>
                    )}
                  </Button>

                  {selectedOrder && (
                    <Button
                      variant="destructive"
                      className={isMobile || isTablet ? "w-full" : "w-40"}
                      size="lg"
                      onClick={() => {
                        if (!selectedOrder) return;
                        if (!confirm(t("Counter.Confirm.CancelTreatmentLong")))
                          return;
                        cancelOrderMutation.mutate(selectedOrder.id);
                      }}
                      disabled={cancelOrderMutation.isPending}
                    >
                      {cancelOrderMutation.isPending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          {t("Counter.Actions.Cancelling")}
                        </>
                      ) : (
                        <>
                          <X className="w-4 h-4 mr-2" />
                          {t("Counter.Actions.CancelTreatment")}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mobile Cart Button - Floating */}
      {isMobile && activeTab === "create" && cart.length > 0 && (
        <button
          onClick={() => setShowMobileCart(true)}
          className="fixed bottom-6 right-6 bg-primary text-primary-foreground rounded-full p-4 shadow-lg hover:shadow-xl transition-all z-50 flex items-center gap-2"
        >
          <ShoppingCart className="w-6 h-6" />
          <Badge className="bg-white text-primary font-bold">
            {cart.length}
          </Badge>
        </button>
      )}

      {/* Mobile Payment Processing Modal */}
      {isMobile && activeTab === "payment" && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-card w-full max-h-[90vh] rounded-t-2xl flex flex-col animate-slide-up">
            {/* Modal Header */}
            <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <h3 className="font-bold text-base flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                {t("Counter.Payment.ProcessPayment")}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedOrder(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Payment Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Order Details */}
              <div className="border-b border-border pb-3">
                <h3 className="text-base font-semibold mb-3">
                  {t("Counter.Payment.PaymentDetails")}
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span>{t("Counter.Payment.Order")}</span>
                    <span className="font-medium">#{selectedOrder.id}</span>
                  </div>
                  {selectedOrder.customer_name && (
                    <div className="flex justify-between">
                      <span>{t("Counter.Payment.Customer")}</span>
                      <span className="font-medium">
                        {selectedOrder.customer_name}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const breakdown = getOrderPaymentBreakdown(selectedOrder);
                    return (
                      <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                        <span>{t("Counter.TreatmentPlan.Total")}</span>
                        <span className="text-primary">
                          {formatCurrency(breakdown.total)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Payment Method Selection */}
              <div>
                <label className="text-xs font-medium mb-2 block">
                  {t("Counter.Payment.PaymentMethod")}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={paymentMethod === "cash" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPaymentMethod("cash")}
                    className="text-xs"
                  >
                    <DollarSign className="w-3 h-3 mr-1" />
                    {t("Counter.Payment.Methods.Cash")}
                  </Button>
                  <Button
                    variant={paymentMethod === "card" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPaymentMethod("card")}
                    className="text-xs"
                  >
                    <CreditCard className="w-3 h-3 mr-1" />
                    {t("Counter.Payment.Methods.Card")}
                  </Button>
                  <Button
                    variant={
                      paymentMethod === "insurance" ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => setPaymentMethod("insurance")}
                    className="text-xs"
                  >
                    <ShoppingCart className="w-3 h-3 mr-1" />
                    {t("Counter.Payment.Methods.Insurance")}
                  </Button>
                  <Button
                    variant={paymentMethod === "others" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPaymentMethod("others")}
                    className="text-xs"
                  >
                    {t("Counter.Payment.Methods.Others")}
                  </Button>
                </div>
              </div>

              {/* Payment Amount */}
              <div>
                <label className="text-xs font-medium mb-1 block">
                  {t("Counter.Payment.PaymentAmount")}
                </label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  className="h-10 text-base"
                />
              </div>

              {/* Reference Number */}
              {paymentMethod !== "cash" && (
                <div>
                  <label className="text-xs font-medium mb-1 block">
                    {t("Counter.Payment.ReferenceNumber")}
                  </label>
                  <Input
                    placeholder={t("Counter.Payment.TransactionReference")}
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    className="h-10 text-sm"
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t border-border bg-card space-y-2">
              <Button
                className="w-full"
                size="lg"
                onClick={handleProcessPayment}
                disabled={!paymentAmount || processPaymentMutation.isPending}
              >
                {processPaymentMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t("Counter.Payment.Processing")}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    {t("Counter.Payment.ProcessPayment")}
                  </>
                )}
              </Button>

              <Button
                variant="destructive"
                className="w-full"
                size="lg"
                onClick={() => {
                  if (!confirm(t("Counter.Confirm.CancelTreatmentLong")))
                    return;
                  cancelOrderMutation.mutate(selectedOrder.id);
                }}
                disabled={cancelOrderMutation.isPending}
              >
                {cancelOrderMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t("Counter.Actions.Cancelling")}
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4 mr-2" />
                    {t("Counter.Actions.CancelTreatment")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Cart Modal */}
      {isMobile && showMobileCart && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-card w-full max-h-[85vh] rounded-t-2xl flex flex-col animate-slide-up">
            {/* Modal Header */}
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                {t("Counter.TreatmentPlan.Title")} ({cart.length})
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMobileCart(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Cart Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {cart.map((item) => (
                <Card key={item.Stock.id} className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{item.Stock.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(item.Stock.cost_per_unit)} ×{" "}
                        {item.quantity}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeFromCart(item.Stock.id)}
                        className="h-8 w-8 p-0"
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 text-center font-medium">
                        {item.quantity}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addToCart(item.Stock)}
                        className="h-8 w-8 p-0"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="font-bold text-sm">
                      {formatCurrency(item.Stock.cost_per_unit * item.quantity)}
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Cart Summary and Actions */}
            <div className="p-4 border-t border-border bg-card space-y-3">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t("Counter.TreatmentPlan.Total")}:</span>
                  <span className="font-bold text-lg">
                    {formatCurrency(getTotalAmount())}
                  </span>
                </div>
              </div>
              <Button
                className="w-full"
                size="lg"
                onClick={() => {
                  handleCreateOrder();
                  setShowMobileCart(false);
                }}
                disabled={cart.length === 0 || createOrderMutation.isPending}
              >
                {createOrderMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t("Counter.Actions.CreatingTreatment")}
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {t("Counter.Actions.CreateTreatment", {
                      type: orderType.replace("_", " "),
                    })}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
